/**
 * Steps Component - Theme-aware step indicator
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;

import { styles } from '../styles';

export interface StepProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  status?: 'wait' | 'process' | 'finish' | 'error';
}

export interface StepsProps {
  current?: number;
  status?: 'wait' | 'process' | 'finish' | 'error';
  direction?: 'horizontal' | 'vertical';
  size?: 'small' | 'default';
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export function Step({ title, description, icon, status }: StepProps) {
  // Step is just a container - actual rendering happens in Steps
  return null;
}

export function Steps({
  current = 0,
  status: currentStatus = 'process',
  direction = 'horizontal',
  size = 'default',
  children,
  style,
}: StepsProps) {
  const steps: StepProps[] = [];
  
  React.Children.forEach(children, (child: any, index: number) => {
    if (React.isValidElement(child) && (child.type === Step || (child.type as any)?.displayName === 'Step')) {
      steps.push({
        title: child.props.title,
        description: child.props.description,
        icon: child.props.icon,
        status: child.props.status,
      });
    }
  });

  const getStepStatus = (index: number): 'wait' | 'process' | 'finish' | 'error' => {
    if (steps[index]?.status) return steps[index].status!;
    if (index < current) return 'finish';
    if (index === current) return currentStatus;
    return 'wait';
  };

  const getStepColor = (status: 'wait' | 'process' | 'finish' | 'error') => {
    switch (status) {
      case 'finish':
        return 'var(--success-color)';
      case 'process':
        return 'var(--main-accent)';
      case 'error':
        return 'var(--error-color)';
      default:
        return 'var(--main-text-secondary)';
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: direction === 'vertical' ? 'column' : 'row',
    width: '100%',
    ...style,
  };

  const stepStyle = (index: number): React.CSSProperties => ({
    flex: 1,
    display: 'flex',
    flexDirection: direction === 'vertical' ? 'row' : 'column',
    alignItems: direction === 'vertical' ? 'flex-start' : 'center',
    position: 'relative',
  });

  const iconSize = size === 'small' ? '24px' : '32px';
  const iconStyle = (stepStatus: 'wait' | 'process' | 'finish' | 'error'): React.CSSProperties => ({
    width: iconSize,
    height: iconSize,
    borderRadius: '50%',
    background: getStepColor(stepStatus),
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size === 'small' ? '12px' : '14px',
    fontWeight: '600',
    marginBottom: direction === 'horizontal' ? '8px' : '0',
    marginRight: direction === 'vertical' ? '12px' : '0',
  });

  return React.createElement('div', { style: containerStyle },
    steps.map((step, index) => {
      const stepStatus = getStepStatus(index);
      const isLast = index === steps.length - 1;

      return React.createElement('div', { key: index, style: stepStyle(index) },
        React.createElement('div', {
          style: {
            display: 'flex',
            flexDirection: direction === 'vertical' ? 'row' : 'column',
            alignItems: 'center',
            position: 'relative',
          }
        },
          React.createElement('div', { style: iconStyle(stepStatus) },
            step.icon || (index + 1)
          ),
          !isLast && direction === 'horizontal' && React.createElement('div', {
            style: {
              position: 'absolute',
              top: `calc(${iconSize} / 2)`,
              left: `calc(50% + ${iconSize} / 2)`,
              right: `calc(-50% + ${iconSize} / 2)`,
              height: '2px',
              background: index < current ? getStepColor('finish') : 'var(--border-color)',
            }
          }),
          !isLast && direction === 'vertical' && React.createElement('div', {
            style: {
              position: 'absolute',
              left: `calc(${iconSize} / 2)`,
              top: iconSize,
              bottom: `calc(-100% + ${iconSize})`,
              width: '2px',
              background: index < current ? getStepColor('finish') : 'var(--border-color)',
            }
          })
        ),
        React.createElement('div', {
          style: {
            textAlign: direction === 'horizontal' ? 'center' : 'left',
            marginTop: direction === 'horizontal' ? '8px' : '0',
          }
        },
          step.title && React.createElement('div', {
            style: {
              fontWeight: '500',
              color: 'var(--main-text)',
              marginBottom: step.description ? '4px' : '0',
            }
          }, step.title),
          step.description && React.createElement('div', {
            style: {
              fontSize: '13px',
              color: 'var(--main-text-secondary)',
            }
          }, step.description)
        )
      );
    })
  );
}

// Attach Step to Steps
(Steps as any).Step = Step;

